/**
 * Installs window-level error reporting.
 *
 * React error boundaries only see failures thrown during render. Everything
 * else — an event handler, a failed upload, a rejected promise in a Stripe
 * callback — bypasses them entirely, and neither app had a `window.onerror`
 * or `unhandledrejection` listener, so none of it was recorded anywhere.
 *
 * Deduplication is not a nicety here. A render loop or a repeatedly failing
 * interval can produce thousands of identical errors in seconds, and without
 * a cap this becomes the noisy-bucket problem the platform's severity work
 * exists to fix — plus a self-inflicted request flood.
 */
import { reportClientError, type ClientErrorApp } from '@dfq/shared/api/clientErrors';
import { consumeCrashMarker } from '@dfq/shared/lib/crashMarker';

/** Signatures already sent, so a repeating failure reports once. */
const seen = new Set<string>();

/** Ceiling per page load, in case signatures vary (a counter in the message). */
const MAX_REPORTS_PER_PAGE = 20;
let sent = 0;

function signature(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}:${error.message}`;
  }

  return typeof error === 'string' ? error : Object.prototype.toString.call(error);
}

function shouldReport(error: unknown): boolean {
  if (sent >= MAX_REPORTS_PER_PAGE) {
    return false;
  }

  const key = signature(error);

  if (seen.has(key)) {
    return false;
  }

  seen.add(key);
  sent += 1;

  return true;
}

/**
 * Report an error if it is new to this page load.
 *
 * Exported so error boundaries share the same budget as the window listeners —
 * a component crashing in a render loop must not exhaust the cap separately.
 */
export function reportIfNew(
  app: ClientErrorApp,
  error: unknown,
  kind: 'render' | 'unhandled' | 'rejection',
  componentStack?: string,
): void {
  if (! shouldReport(error)) {
    return;
  }

  void reportClientError(app, {
    error,
    kind,
    ...(componentStack === undefined ? {} : { componentStack }),
  });
}

/**
 * Attach the listeners. Returns a teardown so tests can uninstall.
 */
export function installClientErrorReporting(app: ClientErrorApp): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const onError = (event: ErrorEvent): void => {
    // `error` is absent for cross-origin script failures, where the browser
    // gives only "Script error." — reporting the message alone is still worth
    // it, since a burst of them is itself the signal.
    reportIfNew(app, event.error ?? event.message, 'unhandled');
  };

  const onRejection = (event: PromiseRejectionEvent): void => {
    reportIfNew(app, event.reason, 'rejection');
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}

/** Test seam — clears the per-page dedup budget. */
export function resetClientErrorReportingState(): void {
  seen.clear();
  sent = 0;
}

/**
 * Reports a crash left by the *previous* page load, if any.
 *
 * A marker still set on boot means the step that wrote it never reached its
 * `clearCrashStep()` call — the renderer died before anything, including this
 * reporter, got a chance to run. Call once, at module scope alongside
 * `installClientErrorReporting`, so a crash on the very first render of the
 * next load is still caught.
 */
export function reportCrashMarkerIfPresent(app: ClientErrorApp): void {
  const marker = consumeCrashMarker();
  if (!marker) return;

  void reportClientError(app, {
    error: new Error(`Renderer crash detected before "${marker.step}" completed`),
    kind: 'renderer_crash',
    step: marker.step,
    ...(marker.context ? { context: marker.context } : {}),
  });
}
